import { cloneDeep, every, findIndex } from 'lodash-es';
class DcuplQueryError extends Error {
    query;
    constructor(message, query) {
        const msg = `Query Error: ${message} in ${JSON.stringify(query, null, 2)}`;
        super(msg);
        this.query = query;
        this.name = 'DcuplQueryError';
    }
}
export class DcuplQueryBuilder {
    _defaultQueryOptions = {
        modelKey: '',
        groupKey: 'root',
        groupType: 'and',
        queries: [],
    };
    _initOptions;
    currentQuery = cloneDeep(this._defaultQueryOptions);
    constructor() { }
    init(options) {
        this._initOptions = cloneDeep(options);
        this.currentQuery = cloneDeep(this._defaultQueryOptions);
        this.currentQuery.modelKey = options.modelKey;
    }
    reset() {
        this.init(this._initOptions);
        return this.getQuery();
    }
    isQuery(query) {
        return 'attribute' in query;
    }
    isQueryGroup(query) {
        return 'groupKey' in query;
    }
    isRootQueryGroup(groupOrQuery) {
        return this.isQueryGroup(groupOrQuery) && groupOrQuery.groupKey === 'root';
    }
    getQueryGroup(groupKey) {
        return this.currentQuery.queries.find((q) => this.isQueryGroup(q) && q.groupKey === groupKey);
    }
    getQuery() {
        return cloneDeep(this.currentQuery);
    }
    hasQueryGroup(groupKey) {
        return this.currentQuery.queries.some((query) => this.isQueryGroup(query) && query.groupKey === groupKey);
    }
    has(options) {
        if (options.groupKey && options.queryKey) {
            const group = this.getQueryGroup(options.groupKey);
            if (group) {
                return group.queries.some((q) => this.isQuery(q) && q.queryKey === options.queryKey);
            }
        }
        else if (options.groupKey) {
            return this.hasQueryGroup(options.groupKey);
        }
        return false;
    }
    validate(query) {
        const errors = query.queries.map((q) => this.validateQuery(q));
        const valid = every(errors, (e) => e.valid);
        return { valid, errors: errors.map((e) => e.errors).flat() };
    }
    applyOptions(options, mode = 'update') {
        if (mode === 'set') {
            this.currentQuery.count = options.count;
            this.currentQuery.start = options.start;
            this.currentQuery.sort = options.sort;
            this.currentQuery.projection = options.projection;
        }
        else {
            if (typeof options?.sort !== 'undefined') {
                this.currentQuery.sort = options.sort;
            }
            if (typeof options?.count !== 'undefined') {
                this.currentQuery.count = options.count;
            }
            if (typeof options?.start !== 'undefined') {
                this.currentQuery.start = options?.start;
            }
            if (typeof options?.projection !== 'undefined') {
                this.currentQuery.projection = options?.projection;
            }
        }
        return this.getQuery();
    }
    applyQuery(queryToApply, options, relevantQuery) {
        if (!relevantQuery)
            relevantQuery = this.currentQuery;
        if (Array.isArray(queryToApply)) {
            for (const query of queryToApply) {
                this.applyQuery(query, options, relevantQuery);
            }
        }
        else if (this.isRootQueryGroup(queryToApply)) {
            if (queryToApply.groupType)
                relevantQuery.groupType = queryToApply.groupType;
            // if (queryToApply.options) relevantQuery.options = queryToApply.options;
            if (options?.mode === 'set') {
                relevantQuery.queries = queryToApply.queries;
            }
            else {
                relevantQuery.queries = [...relevantQuery.queries, ...queryToApply.queries];
            }
            this.applyOptions(queryToApply, 'set');
        }
        else if (this.isQuery(queryToApply)) {
            const idx = relevantQuery.queries.findIndex((query) => this.isQueryGroup(query) && query.groupKey === queryToApply.attribute);
            if (idx > -1) {
                const queryGroup = relevantQuery.queries[idx];
                if (options?.mode === 'set') {
                    queryGroup.queries = [queryToApply];
                }
                else {
                    queryGroup.queries.push(queryToApply);
                }
            }
            else {
                relevantQuery.queries.push({
                    groupKey: queryToApply.attribute,
                    groupType: 'or',
                    queries: [queryToApply],
                });
            }
            // handle query group
        }
        else if (this.isQueryGroup(queryToApply)) {
            const idx = relevantQuery.queries.findIndex((query) => this.isQueryGroup(query) && query.groupKey === queryToApply.groupKey);
            if (idx > -1) {
                const queryGroup = relevantQuery.queries[idx];
                if (queryToApply.groupType)
                    queryGroup.groupType = queryToApply.groupType;
                // if (queryToApply.options) queryGroup.options = queryToApply.options;
                if (options?.mode === 'set') {
                    relevantQuery.queries[idx] = queryToApply;
                }
                else {
                    queryGroup.queries = [...queryGroup.queries, ...queryToApply.queries];
                }
            }
            else {
                relevantQuery.queries.push({
                    groupKey: queryToApply.groupKey,
                    groupType: queryToApply.groupType || 'or',
                    queries: queryToApply.queries,
                    // options: queryToApply.options,
                });
            }
        }
        return relevantQuery;
    }
    removeAllQueries() {
        this.currentQuery.queries = [];
        return this.getQuery();
    }
    removeQuery(queryToRemove, relevantQuery) {
        // handle default query
        if (!relevantQuery)
            relevantQuery = this.currentQuery;
        if (this.isQuery(queryToRemove)) {
            const idx = relevantQuery.queries.findIndex((query) => this.isQueryGroup(query) && query.groupKey === queryToRemove.attribute);
            if (idx > -1) {
                const queryGroup = relevantQuery.queries[idx];
                const idxOfQueryToRemove = findIndex(queryGroup.queries, queryToRemove);
                if (idxOfQueryToRemove > -1) {
                    queryGroup.queries.splice(idxOfQueryToRemove, 1);
                }
                if (queryGroup.queries.length === 0) {
                    relevantQuery.queries.splice(idx, 1);
                }
            }
        }
        else if (this.isQueryGroup(queryToRemove)) {
            const idx = relevantQuery.queries.findIndex((query) => this.isQueryGroup(query) && query.groupKey === queryToRemove.groupKey);
            if (idx > -1) {
                if (!queryToRemove.queries) {
                    relevantQuery.queries.splice(idx, 1);
                }
                else {
                    const queryGroup = relevantQuery.queries[idx];
                    for (const q of queryToRemove.queries) {
                        const idxOfQueryToRemove = findIndex(queryGroup.queries, q);
                        if (idxOfQueryToRemove > -1) {
                            queryGroup.queries.splice(idxOfQueryToRemove, 1);
                        }
                    }
                    if (queryGroup.queries.length === 0) {
                        relevantQuery.queries.splice(idx, 1);
                    }
                }
            }
        }
        return relevantQuery;
    }
    validateQuery(query) {
        const errors = [];
        if (this.isQuery(query)) {
            if (!query.attribute) {
                errors.push(new DcuplQueryError('Missing attribute', query));
            }
            if (!query.operator) {
                errors.push(new DcuplQueryError('Missing operator', query));
            }
            if (typeof query.value === 'undefined') {
                errors.push(new DcuplQueryError('Missing value', query));
            }
            if (query.operator === 'find' && Array.isArray(query.value)) {
                errors.push(new DcuplQueryError('find operator cannot have array value', query));
            }
            if (errors.length > 0) {
                return { valid: false, errors: errors };
            }
            else {
                return { valid: true };
            }
        }
        else if (this.isQueryGroup(query)) {
            const queryErrors = query.queries.map((q) => this.validateQuery(q));
            const invalidQueries = queryErrors.filter((q) => !q.valid);
            const errors = invalidQueries.map((q) => q.errors).flat();
            if (errors.length > 0) {
                return { valid: false, errors: errors };
            }
            else {
                return { valid: true };
            }
        }
        errors.push(new DcuplQueryError('Invalid query definition', query));
        return { valid: false, errors: errors };
    }
}
//# sourceMappingURL=query-builder.js.map